/**
 * PostsPage - lista + editor de publicações
 * Melhorias: filtro por canal, pesquisa por texto, AlertDialog para eliminação
 */
import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "@/components/ui/tabs";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Plus, Send, Trash2, Edit3, FileEdit, Film, Play, Search } from "lucide-react";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/shared/StatusBadge";
import ChannelIcon from "@/components/shared/ChannelIcon";
import { fmtDateTime } from "@/lib/date";
import EmptyState from "@/components/shared/EmptyState";
import PostEditor from "@/components/app/PostEditor";
import { getPostFormat } from "@/lib/postFormats";

export default function PostsPage() {
    const [params, setParams] = useSearchParams();
    const [posts, setPosts] = useState([]);
    const [tab, setTab] = useState("todas");
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [search, setSearch] = useState("");
    const [channelFilter, setChannelFilter] = useState("all");
    const [deleteTarget, setDeleteTarget] = useState(null); // post a eliminar

    const load = () => api.get("/posts").then((r) => setPosts(r.data));
    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (params.get("new") === "1") {
            setEditing(null);
            setEditorOpen(true);
            params.delete("new");
            setParams(params, { replace: true });
        }
    }, [params, setParams]);

    // Canais únicos presentes nos posts
    const availableChannels = useMemo(() => {
        const set = new Set();
        posts.forEach((p) => p.channels?.forEach((c) => set.add(c)));
        return Array.from(set).sort();
    }, [posts]);

    const applyFilters = (list) => {
        return list.filter((p) => {
            const matchSearch = !search ||
                p.caption?.toLowerCase().includes(search.toLowerCase()) ||
                p.hashtags?.some((h) => h.toLowerCase().includes(search.toLowerCase()));
            const matchChannel = channelFilter === "all" || p.channels?.includes(channelFilter);
            return matchSearch && matchChannel;
        });
    };

    const grouped = useMemo(() => {
        return {
            todas: applyFilters(posts),
            rascunho: applyFilters(posts.filter((p) => p.status === "rascunho")),
            agendado: applyFilters(posts.filter((p) => p.status === "agendado")),
            publicado: applyFilters(posts.filter((p) => p.status === "publicado")),
            falhado: applyFilters(posts.filter((p) => p.status === "falhado")),
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [posts, search, channelFilter]);

    const openNew = () => {
        setEditing(null);
        setEditorOpen(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setEditorOpen(true);
    };

    const confirmDelete = (p) => setDeleteTarget(p);

    const remove = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/posts/${deleteTarget.id}`);
            toast.success("Publicação eliminada");
            load();
        } catch (err) {
            toast.error(getApiErrorMessage(err));
        } finally {
            setDeleteTarget(null);
        }
    };

    const publishNow = async (p) => {
        try {
            await api.post(`/posts/${p.id}/publish-now`);
            toast.success("Publicação publicada agora!");
            load();
        } catch (err) {
            toast.error(getApiErrorMessage(err));
        }
    };

    return (
        <div className="space-y-6" data-testid="posts-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Publicações</h1>
                    <p className="text-muted-foreground">Crie, agende e gerencie todas as suas publicações.</p>
                </div>
                <Button className="btn-grad-purple text-white" onClick={openNew} data-testid="posts-new-button">
                    <Plus className="h-4 w-4 mr-2" /> Nova publicação
                </Button>
            </div>

            {/* Filtros de pesquisa e canal */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Pesquisar por texto ou hashtag..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                        data-testid="posts-search-input"
                    />
                </div>
                {availableChannels.length > 0 && (
                    <Select value={channelFilter} onValueChange={setChannelFilter}>
                        <SelectTrigger className="w-full sm:w-44" data-testid="posts-channel-filter">
                            <SelectValue placeholder="Filtrar por canal" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos os canais</SelectItem>
                            {availableChannels.map((c) => (
                                <SelectItem key={c} value={c}>
                                    <span className="capitalize">{c}</span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="posts-tabs">
                    <TabsTrigger value="todas" data-testid="posts-tab-todas">Todas <Badge variant="secondary" className="ml-2">{grouped.todas.length}</Badge></TabsTrigger>
                    <TabsTrigger value="rascunho" data-testid="posts-tab-rascunho">Rascunhos <Badge variant="secondary" className="ml-2">{grouped.rascunho.length}</Badge></TabsTrigger>
                    <TabsTrigger value="agendado" data-testid="posts-tab-agendado">Agendadas <Badge variant="secondary" className="ml-2">{grouped.agendado.length}</Badge></TabsTrigger>
                    <TabsTrigger value="publicado" data-testid="posts-tab-publicado">Publicadas <Badge variant="secondary" className="ml-2">{grouped.publicado.length}</Badge></TabsTrigger>
                    <TabsTrigger value="falhado" data-testid="posts-tab-falhado">Falhadas <Badge variant="secondary" className="ml-2">{grouped.falhado.length}</Badge></TabsTrigger>
                </TabsList>
                {Object.entries(grouped).map(([k, items]) => (
                    <TabsContent key={k} value={k} className="mt-4">
                        {items.length === 0 ? (
                            <EmptyState
                                icon={FileEdit}
                                title="Sem publicações"
                                description="Ainda não tem publicações nesta categoria. Crie a primeira e deixe o piloto automático trabalhar."
                                ctaLabel="Criar publicação"
                                onCta={openNew}
                                testid="posts-empty"
                            />
                        ) : (
                            <div className="grid gap-3">
                                {items.map((p) => (
                                    <Card
                                        key={p.id}
                                        className="glass-card p-4 rounded-2xl border-white/10 flex flex-col sm:flex-row gap-4"
                                        data-testid={`post-row-${p.id}`}
                                    >
                                        {p.media?.[0]?.url && (
                                            <div className="relative w-full sm:w-32 h-32 shrink-0">
                                                {p.media[0].type === "video" ? (
                                                    <>
                                                        <video
                                                            src={p.media[0].url}
                                                            className="w-full h-full object-cover rounded-xl bg-black"
                                                            preload="metadata"
                                                            muted
                                                        />
                                                        <div className="absolute inset-0 grid place-items-center pointer-events-none">
                                                            <div className="h-9 w-9 rounded-full bg-black/55 grid place-items-center">
                                                                <Play className="h-4 w-4 text-white" fill="currentColor" />
                                                            </div>
                                                        </div>
                                                        <Badge className="absolute top-1.5 left-1.5 bg-black/65 text-white border-none gap-1 text-[9px] px-1.5 py-0">
                                                            <Film className="h-2.5 w-2.5" /> Vídeo
                                                        </Badge>
                                                    </>
                                                ) : (
                                                    <img
                                                        src={p.media[0].url}
                                                        alt=""
                                                        className="w-full h-full object-cover rounded-xl"
                                                    />
                                                )}
                                                {p.post_format && p.post_format !== "feed" && (
                                                    <Badge className="absolute bottom-1.5 right-1.5 bg-[#6B21D9]/85 text-white border-none text-[9px] px-1.5 py-0">
                                                        {getPostFormat(p.post_format).label}
                                                    </Badge>
                                                )}
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <StatusBadge status={p.status} />
                                                    {p.channels?.map((ch) => (
                                                        <ChannelIcon key={ch} type={ch} showBg className="h-3 w-3" />
                                                    ))}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {p.status === "agendado" && fmtDateTime(p.scheduled_for)}
                                                    {p.status === "publicado" && fmtDateTime(p.published_at)}
                                                </div>
                                            </div>
                                            <p className="mt-2 text-sm line-clamp-3">{p.caption}</p>
                                            {p.hashtags?.length > 0 && (
                                                <p className="mt-1 text-xs text-[#A78BFA]">
                                                    {p.hashtags.slice(0, 6).map((h) => `#${h}`).join(" ")}
                                                </p>
                                            )}
                                            {p.metrics && (
                                                <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                                                    <span>👁️ {p.metrics.reach}</span>
                                                    <span>❤️ {p.metrics.likes}</span>
                                                    <span>💬 {p.metrics.comments}</span>
                                                    <span>↗️ {p.metrics.shares}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex sm:flex-col gap-2 sm:items-end">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => openEdit(p)}
                                                data-testid={`post-edit-${p.id}`}
                                            >
                                                <Edit3 className="h-3.5 w-3.5" />
                                            </Button>
                                            {p.status !== "publicado" && (
                                                <Button
                                                    size="sm"
                                                    onClick={() => publishNow(p)}
                                                    className="btn-grad-purple text-white"
                                                    data-testid={`post-publish-now-${p.id}`}
                                                >
                                                    <Send className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => confirmDelete(p)}
                                                data-testid={`post-delete-${p.id}`}
                                            >
                                                <Trash2 className="h-3.5 w-3.5 text-[#FB7185]" />
                                            </Button>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                ))}
            </Tabs>

            <PostEditor
                open={editorOpen}
                onOpenChange={setEditorOpen}
                post={editing}
                onSaved={() => {
                    load();
                    setEditorOpen(false);
                }}
            />

            {/* AlertDialog de confirmação de eliminação */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Eliminar publicação?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Esta ação é irreversível. A publicação será eliminada permanentemente.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={remove}
                            className="bg-[#FB7185] hover:bg-[#f43f5e] text-white"
                            data-testid="posts-delete-confirm"
                        >
                            Eliminar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
